import type { BandScale, LinearScale } from './scale';
import type { Formatter, PlotBox } from './types';

/**
 * Axes and gridlines.
 *
 * Every element here is `aria-hidden`. The axis is scaffolding for reading the
 * drawing; a screen reader gets the numbers themselves from `ChartTable`, and
 * announcing "0, 20, 40, 60, 80, 100" before the data would just be a second
 * thing to skip past.
 */

interface GridlinesProps {
  y: LinearScale;
  plot: PlotBox;
  ticks: number[];
}

export function Gridlines({ y, plot, ticks }: GridlinesProps) {
  return (
    <g aria-hidden="true">
      {ticks.map((tick) => (
        <line
          key={tick}
          className="c-chart__grid"
          x1={0}
          x2={plot.inner.width}
          y1={y(tick)}
          y2={y(tick)}
          // A zero line is the baseline, not a gridline — bars sit on it and
          // it has to read as structure rather than as one rule among five.
          strokeWidth={tick === 0 ? 1 : 1}
          opacity={tick === 0 ? 1 : undefined}
        />
      ))}
    </g>
  );
}

interface AxisLeftProps {
  y: LinearScale;
  ticks: number[];
  format: Formatter;
}

export function AxisLeft({ y, ticks, format }: AxisLeftProps) {
  return (
    <g aria-hidden="true">
      {ticks.map((tick) => (
        <text
          key={tick}
          className="c-chart__tick c-chart__tick--y"
          x={-8}
          y={y(tick)}
          textAnchor="end"
          dominantBaseline="middle"
        >
          {format(tick)}
        </text>
      ))}
    </g>
  );
}

interface AxisBottomProps {
  x: BandScale;
  plot: PlotBox;
  labels: ReadonlyMap<string, string>;
  /**
   * Draw every nth label.
   *
   * Thinning beats shrinking: below about 10px axis text stops being readable
   * and starts being texture, so a narrow chart shows fewer dates rather than
   * smaller ones. The first and last are always kept — a time axis whose ends
   * are unlabelled does not say what period it covers.
   */
  every?: number | undefined;
}

export function AxisBottom({ x, plot, labels, every = 1 }: AxisBottomProps) {
  const keys = x.keys;
  const last = keys.length - 1;

  return (
    <g aria-hidden="true">
      {keys.map((key, index) => {
        if (every > 1 && index % every !== 0 && index !== last) return null;
        // Dropping the second-to-last when it would collide with the last,
        // which is what the modulo leaves behind at awkward series lengths.
        if (every > 1 && index !== last && last - index < every / 2) return null;

        return (
          <text
            key={key}
            className="c-chart__tick"
            x={x.centre(key)}
            y={plot.inner.height + 18}
            textAnchor="middle"
          >
            {labels.get(key) ?? key}
          </text>
        );
      })}
    </g>
  );
}

/**
 * How many labels a categorical axis can carry.
 *
 * Estimated from the longest label rather than a fixed count, because "Jan" and
 * "Haematology" need very different room.
 */
export function labelStride(count: number, width: number, longestLabel: number): number {
  if (count === 0) return 1;
  // ~6.5px per character at --text-label, plus a gutter.
  const needed = longestLabel * 6.5 + 12;
  const fits = Math.max(1, Math.floor(width / needed));
  return Math.max(1, Math.ceil(count / fits));
}
